/* Team chat (say_team) on the admin match page: one card per team, each with a player filter.
 * Entries come from MatchDetail's chat list (is_team = true), already filtered by map.
 * team_no (1/2) is resolved by the server from the roster, so a card keeps its team after the side swap. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './teamChat.css';

const SIDE_COLOR = { CT: '#22d3ee', TERRORIST: '#fb923c', T: '#fb923c' };

function timeOf(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

function TeamCard({ name, side, entries, autoScroll }) {
  const { t } = useTranslation();
  const [player, setPlayer] = useState(null); // steam id (or name) to filter by
  const listRef = useRef(null);

  const players = useMemo(() => {
    const map = new Map();
    entries.forEach(e => {
      const key = e.steam_id || e.player_name || '?';
      const p = map.get(key) || { key, name: e.player_name || 'Player', count: 0 };
      p.name = e.player_name || p.name; // latest in-game name
      p.count += 1;
      map.set(key, p);
    });
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [entries]);

  const shown = player ? entries.filter(e => (e.steam_id || e.player_name || '?') === player) : entries;

  useEffect(() => {
    if (autoScroll && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [shown.length, autoScroll]);

  const sideLabel = side === 'CT' ? 'CT' : side ? 'T' : null;
  return (
    <div className="tc-card" style={{ '--tc': SIDE_COLOR[side] || '#94a3b8' }}>
      <div className="tc-head">
        <span className="tc-dot" />
        <span className="tc-name" title={name}>{name}</span>
        {sideLabel && <span className="tc-side">{sideLabel}</span>}
        <span className="tc-count">{t('teamChat.messages', '{{count}} messages', { count: entries.length })}</span>
      </div>
      {players.length > 1 && (
        <div className="tc-players">
          <button type="button" className={`tc-chip${player == null ? ' on' : ''}`} onClick={() => setPlayer(null)}>{t('teamChat.all', 'All')}</button>
          {players.map(p => (
            <button type="button" key={p.key} className={`tc-chip${player === p.key ? ' on' : ''}`} onClick={() => setPlayer(player === p.key ? null : p.key)}>
              {p.name}<span>{p.count}</span>
            </button>
          ))}
        </div>
      )}
      <div className="tc-list" ref={listRef}>
        {shown.length === 0 ? (
          <div className="tc-empty">{t('teamChat.empty', 'No team chat yet.')}</div>
        ) : shown.map((e, i) => (
          <div key={i} className="tc-msg">
            <span className="tc-time">{timeOf(e.ts)}</span>
            <span className="tc-line">
              <span className="tc-player" style={{ color: SIDE_COLOR[(e.team || '').toUpperCase()] || 'var(--text-secondary)' }}>{e.player_name || 'Player'}</span>
              <span className="tc-text">{e.chat_message}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TeamChatPanel({ entries, team1Name, team2Name, team1Side, team2Side, autoScroll }) {
  const { t } = useTranslation();
  const t1 = entries.filter(e => Number(e.team_no) === 1);
  const t2 = entries.filter(e => Number(e.team_no) === 2);
  const other = entries.filter(e => Number(e.team_no) !== 1 && Number(e.team_no) !== 2);
  return (
    <div className="tc-wrap">
      <div className="tc-grid">
        <TeamCard name={team1Name || 'Team 1'} side={team1Side} entries={t1} autoScroll={autoScroll} />
        <TeamCard name={team2Name || 'Team 2'} side={team2Side} entries={t2} autoScroll={autoScroll} />
      </div>
      {other.length > 0 && (
        <TeamCard name={t('teamChat.spectators', 'Spectator team chat')} side={null} entries={other} autoScroll={autoScroll} />
      )}
    </div>
  );
}
